import {ActionIcon, Divider, Group, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {Entity, getEntityIdentifier, metersSelector, useGetMetersByObjectQuery} from "@ots-frontend/ots-data";
import {Card, CardProps} from "../Card/Card";
import {useNavigate} from "react-router-dom";
import {IconPlus} from "@tabler/icons-react";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../../providers/RegionProvider/RegionProvider"
import {MeterDataTable} from "../../data-table/MeterDataTable/MeterDataTable"

export type ObjectMeterDataTableCardProps = CardProps & {
  entity: Entity
}

export const ObjectMeterDataTableCard = (props: ObjectMeterDataTableCardProps) => {
  const {
    entity,
    shadow = "sm",
    padding = "lg",
    radius = "md",
    withBorder = true,
    ...rest
  } = props

  const {t} = useTranslation(["common"])
  const navigate = useNavigate()
  const {regionId} = useApp()

  const selectMeters = useMemo(() => {
    return metersSelector();
  }, []);

  const {meters, isMetersLoading} = useGetMetersByObjectQuery(regionId ? {
    id: entity.id,
    type: entity.entityType,
    regionId: regionId,
  } : skipToken, {
    selectFromResult: (result) => ({
      meters: selectMeters(result, {showDoNotUse: true}),
      isMetersLoading: result.isLoading
    })
  });

  const onNewClick = useCallback(() => {
    navigate("/meters/form", {
      state: {
        id: undefined,
        formDefaults: {
          object: getEntityIdentifier(entity)
        }
      }
    });
  }, [navigate, entity])

  return (
    <Card shadow={shadow} padding={padding} radius={radius} withBorder={withBorder} {...rest}>
      <Group position="apart" pb="sm">
        <Title order={4}>{t("common.entity.meter.plural", "Meters")}</Title>
        <Group spacing={4} position="left" noWrap>
          <HasAccess permissions={["meter_edit"]}>
            <ActionIcon color="green" onClick={() => onNewClick()}>
              <IconPlus size={16}/>
            </ActionIcon>
          </HasAccess>
        </Group>
      </Group>

      <Divider pb="sm"/>

      <MeterDataTable
        withBorder
        records={meters}
        fetching={isMetersLoading}
      />
    </Card>
  )
}
